import React from 'react';
import {
    AppointmentList,
    AppointmentsAttachmentData,
    ClinicAttachmentData,
    ClinicsAttachment,
    ClinicsAttachmentData,
    LiveChat,
    LiveChatAttachmentData,
    LiveChatRedirect,
    MedicationList,
    MedicationListAttachmentData,
    MedicationListv2,
    MedicationListAttachmentDatav2,
    MessageAssistQnaResponse,
    MessageAssistQnaData,
    MessageProvider,
    MessageProviderAttachmentData,
    OldVirtualVisitCard,
    OldVirtualVisitCardData,
    PCPCardAttachment,
    PCPCardAttachmentData,
    Timeslots,
    TimeslotAttachmentData,
    VirtualVisitCard,
    VirtualVisitCardData,
    ProvidenceButtonList,
    ProvidenceButtonListData,
} from '.';
import { Activity, Attachment, ReactRenderAttachmentData } from '../types';
import ClinicCardList from './ClinicCardList';
import {
    LiveChatUnavailable,
    LiveChatUnavailableData,
} from './LiveChatUnavailable';
import { InputMode } from '../hooks';
import { AmpFlagSet } from '../amplitude';
import { ApplicationModalPropsType } from '../jsxElements';

type ElementForAttachmentArgs = {
    attachment: Attachment;
    disableButtons: boolean;
    featureFlags: AmpFlagSet;
    appendMessage?: (message: Activity) => void;
    sendMessage: (message: string) => void;
    sendPostBack: (message: string) => void;
    sendEvent: (name: string, value: any) => void;
    setInputMode: (inputMode: InputMode) => void;
    setModalProperties:
        | ((modalProperties: ApplicationModalPropsType) => void)
        | undefined;
    mostRecentMessage: boolean;
};

export const ElementForAttachment: React.FC<ElementForAttachmentArgs> = ({
    attachment,
    disableButtons,
    featureFlags,
    appendMessage,
    sendMessage,
    sendPostBack,
    sendEvent,
    setInputMode,
    setModalProperties,
    mostRecentMessage,
}: ElementForAttachmentArgs) => {
    const { contentType, content } = attachment;

    switch (contentType) {
        case 'application/vnd.microsoft.card.adaptive': {
            return null;
        }

        case 'application/providence.messageAssistTimeoutMessage': {
            return (
                <ProvidenceButtonList
                    data={content as ProvidenceButtonListData}
                    featureFlags={featureFlags}
                    overrideContainerStyle={true}
                    disableButtons={false}
                    sendMessage={sendMessage}
                    sendPostBack={sendPostBack}
                />
            );
        }

        case 'application/providence.messageAssistQnaResponse': {
            return (
                <MessageAssistQnaResponse
                    data={content as MessageAssistQnaData}
                    appendMessage={appendMessage}
                    sendPostBack={sendPostBack}
                />
            );
        }

        case 'application/providence.appointmentCards': {
            return (
                <AppointmentList
                    data={content as AppointmentsAttachmentData}
                    featureFlags={featureFlags}
                />
            );
        }

        case 'application/providence.buttonlist': {
            return null;
        }

        // TODO: Remove the hack when experiment is done.
        case 'application/providence.liveChatButtonlist': {
            return (
                <ProvidenceButtonList
                    data={content as ProvidenceButtonListData}
                    featureFlags={featureFlags}
                    overrideContainerStyle={true}
                    disableButtons={false}
                    sendMessage={sendMessage}
                    sendPostBack={sendPostBack}
                />
            );
        }

        case 'application/providence.timeslots': {
            return (
                <Timeslots
                    data={content as TimeslotAttachmentData}
                    disableButtons={disableButtons}
                    featureFlags={featureFlags}
                    sendMessage={sendMessage}
                />
            );
        }

        case 'application/providence.liveChat': {
            return (
                <LiveChat
                    data={content as LiveChatAttachmentData}
                    featureFlags={featureFlags}
                    sendEvent={sendEvent}
                    sendPostBack={sendPostBack}
                    mostRecentMessage={mostRecentMessage}
                />
            );
        }

        case 'application/providence.liveChatRedirect': {
            return (
                <LiveChatRedirect
                    sendPostBack={sendPostBack}
                    disableButtons={disableButtons}
                />
            );
        }

        case 'application/providence.liveChatUnavailable': {
            return (
                <LiveChatUnavailable
                    data={content as LiveChatUnavailableData}
                />
            );
        }

        case 'application/providence.medicationList': {
            const medicationListData = content as MedicationListAttachmentData;
            return (
                <MedicationList
                    featureFlags={featureFlags}
                    pharmacies={medicationListData.pharmacies}
                />
            );
        }

        case 'application/providence.medicationListv2': {
            const medicationListData =
                content as MedicationListAttachmentDatav2;
            return (
                <MedicationListv2
                    featureFlags={featureFlags}
                    prescriptionData={medicationListData.prescriptionData}
                    hidePhoneNumbers={medicationListData.hidePhoneNumbers}
                    hideInstructions={
                        medicationListData.hideInstructions || false
                    }
                    sendMessage={sendMessage}
                />
            );
        }

        case 'application/providence.messageProvider': {
            return (
                <MessageProvider
                    data={content as MessageProviderAttachmentData}
                    disableButtons={disableButtons}
                    featureFlags={featureFlags}
                    sendMessage={sendMessage}
                    sendPostBack={sendPostBack}
                    setInputMode={setInputMode}
                    setModalProperties={setModalProperties}
                />
            );
        }

        case 'application/providence.clinicCards': {
            return (
                <ClinicsAttachment
                    data={content as ClinicsAttachmentData}
                    disableButtons={disableButtons}
                    featureFlags={featureFlags}
                    sendMessage={sendMessage}
                    sendPostBack={sendPostBack}
                />
            );
        }

        case 'application/providence.pcpCard': {
            return (
                <PCPCardAttachment
                    data={content as PCPCardAttachmentData}
                    disableButtons={disableButtons}
                    featureFlags={featureFlags}
                    sendMessage={sendMessage}
                    sendPostBack={sendPostBack}
                />
            );
        }

        case 'application/providence.oldCarousel': {
            return (
                <ClinicCardList
                    clinicData={content as ClinicAttachmentData}
                    featureFlags={featureFlags}
                />
            );
        }

        case 'application/vnd.microsoft.card.hero': {
            return null;
        }

        case 'application/providence.virtualVisit': {
            return (
                <VirtualVisitCard
                    data={content as VirtualVisitCardData}
                    featureFlags={featureFlags}
                />
            );
        }

        case 'application/reactRender': {
            switch ((content as ReactRenderAttachmentData).renderer) {
                case 'VirtualVisitCard': {
                    return (
                        <OldVirtualVisitCard
                            data={content as OldVirtualVisitCardData}
                        />
                    );
                }

                default: {
                    return <pre>{JSON.stringify(attachment, null, 2)}</pre>;
                }
            }
        }

        default: {
            return <pre>{JSON.stringify(attachment, null, 2)}</pre>;
        }
    }
};
